import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronLeft,
    Video,
    Grid,
    Smartphone,
    Filter,
    Share2,
    AlertTriangle,
    User,
    Truck,
    Activity,
    Plane,
    Play,
    Pause,
    SkipBack,
    SkipForward,
    ZoomIn,
    ZoomOut,
    Maximize2,
    ChevronUp,
    ChevronDown,
    ChevronRight,
    Settings,
    Siren,
    Thermometer,
    Battery,
    Wifi,
    Download,
    RefreshCw,
    Bell,
    BellOff,
    Volume2,
    VolumeX,
    Camera,
    Radio,
    Eye,
    EyeOff,
    Clock,
    Calendar,
    MapPin,
    Shield,
    Zap,
    Moon,
    Sun,
    Mic,
    MicOff,
    RotateCcw,
    Crosshair,
    Move,
    Focus
} from 'lucide-react';
import { toast } from 'sonner';

interface SurveillanceHubProps {
    well: any;
    onClose: () => void;
    isDarkMode?: boolean;
}

interface CameraFeed {
    id: number;
    name: string;
    status: 'online' | 'offline' | 'recording' | 'maintenance';
    type: 'ptz' | 'fixed' | 'thermal' | 'drone';
    battery?: number;
    signal?: number;
    resolution?: string;
    location?: string;
    lastMotion?: string;
    recordingHours?: number;
    storageUsed?: number;
    nightVision?: boolean;
    audioEnabled?: boolean;
}

interface CameraEvent {
    id: number;
    camId: number;
    time: string;
    type: 'person' | 'vehicle' | 'safety' | 'motion' | 'system' | 'fire' | 'wildlife' | 'weather';
    message: string;
    severity: 'info' | 'warning' | 'critical';
    thumbnail?: string;
    acknowledged?: boolean;
}

export default function SurveillanceHub({ well, onClose, isDarkMode = true }: SurveillanceHubProps) {
    // State Management
    const [cameras, setCameras] = useState<CameraFeed[]>([]);
    const [activeCamId, setActiveCamId] = useState<number>(1);
    const [viewMode, setViewMode] = useState<'single' | 'grid' | 'quad'>('single');
    const [events, setEvents] = useState<CameraEvent[]>([]);
    const [isPlaying, setIsPlaying] = useState(true);
    const [timelinePos, setTimelinePos] = useState(100);
    const [showPTZ, setShowPTZ] = useState(false);
    const [filterType, setFilterType] = useState<string>('all');
    const [isThermalMode, setIsThermalMode] = useState(false);
    const [isNightVision, setIsNightVision] = useState(false);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [showSettings, setShowSettings] = useState(false);
    const [showEventDetails, setShowEventDetails] = useState<CameraEvent | null>(null);
    const [recordingMode, setRecordingMode] = useState<'live' | 'playback'>('live');
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [motionZones, setMotionZones] = useState(true);
    const [autoTrack, setAutoTrack] = useState(false);
    const [streamQuality, setStreamQuality] = useState<'auto' | 'high' | 'medium' | 'low'>('auto');

    // Initialize mock data
    useEffect(() => {
        setCameras([
            { id: 1, name: 'Wellhead Main', status: 'recording', type: 'ptz', battery: 100, signal: 98, resolution: '4K HDR', location: 'Wellhead', lastMotion: '2 min ago', recordingHours: 720, storageUsed: 45, nightVision: true, audioEnabled: true },
            { id: 2, name: 'Tank Battery A', status: 'online', type: 'fixed', battery: 85, signal: 92, resolution: '2K', location: 'Tank Farm North', lastMotion: '15 min ago', recordingHours: 720, storageUsed: 38, nightVision: true, audioEnabled: false },
            { id: 3, name: 'Gate Entry', status: 'online', type: 'fixed', battery: 42, signal: 70, resolution: '1080p', location: 'Main Entrance', lastMotion: 'Just now', recordingHours: 720, storageUsed: 52, nightVision: true, audioEnabled: true },
            { id: 4, name: 'Separator Skid', status: 'recording', type: 'thermal', battery: 90, signal: 88, resolution: 'Thermal 640', location: 'Production Area', lastMotion: '5 min ago', recordingHours: 720, storageUsed: 28, nightVision: false, audioEnabled: false },
            { id: 5, name: 'Flare Stack', status: 'online', type: 'drone', battery: 65, signal: 45, resolution: '4K', location: 'Flare Area', lastMotion: '1 hr ago', recordingHours: 120, storageUsed: 15, nightVision: false, audioEnabled: false },
            { id: 6, name: 'Perimeter North', status: 'offline', type: 'fixed', battery: 0, signal: 0, resolution: 'N/A', location: 'North Fence', lastMotion: 'Unknown', recordingHours: 0, storageUsed: 0, nightVision: true, audioEnabled: false },
            { id: 7, name: 'Pump Jack #1', status: 'online', type: 'ptz', battery: 78, signal: 85, resolution: '4K', location: 'Pump Area', lastMotion: '30 sec ago', recordingHours: 720, storageUsed: 41, nightVision: true, audioEnabled: true },
            { id: 8, name: 'Storage Yard', status: 'recording', type: 'fixed', battery: 92, signal: 94, resolution: '2K', location: 'Equipment Yard', lastMotion: '3 min ago', recordingHours: 720, storageUsed: 55, nightVision: true, audioEnabled: false },
        ]);

        setEvents([
            { id: 1, camId: 1, time: '10:42 AM', type: 'person', message: 'Person Detected - Worker ID Verified', severity: 'info', acknowledged: true },
            { id: 2, camId: 4, time: '10:45 AM', type: 'safety', message: 'High Temperature Alert - 185°F', severity: 'critical', acknowledged: false },
            { id: 3, camId: 3, time: '11:15 AM', type: 'vehicle', message: 'Truck Arrival - License: TX-4521', severity: 'info', acknowledged: true },
            { id: 4, camId: 3, time: '11:17 AM', type: 'system', message: 'Gate Access Granted - Employee Badge', severity: 'info', acknowledged: true },
            { id: 5, camId: 2, time: '11:30 AM', type: 'motion', message: 'Motion Detected - Tank Area', severity: 'warning', acknowledged: false },
            { id: 6, camId: 1, time: '12:05 PM', type: 'person', message: 'Maintenance Crew Arrived (3 people)', severity: 'info', acknowledged: true },
            { id: 7, camId: 5, time: '12:10 PM', type: 'fire', message: 'Flare Anomaly Detected - Investigating', severity: 'critical', acknowledged: false },
            { id: 8, camId: 7, time: '12:25 PM', type: 'motion', message: 'Pump Activity Normal', severity: 'info', acknowledged: true },
            { id: 9, camId: 8, time: '12:30 PM', type: 'vehicle', message: 'Unknown Vehicle in Storage Yard', severity: 'warning', acknowledged: false },
            { id: 10, camId: 3, time: '12:45 PM', type: 'wildlife', message: 'Wildlife Movement - Deer', severity: 'info', acknowledged: true },
        ]);
    }, [well]);

    const activeCam = cameras.find(c => c.id === activeCamId) || cameras[0];

    if (!activeCam) {
        return (
            <div className={`fixed inset-0 z-50 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const filteredEvents = filterType === 'all'
        ? events
        : events.filter(e => e.type === filterType || (filterType === 'alert' && e.severity !== 'info'));

    const criticalCount = events.filter(e => e.severity === 'critical' && !e.acknowledged).length;
    const warningCount = events.filter(e => e.severity === 'warning' && !e.acknowledged).length;

    // Handlers
    const handlePTZ = (direction: string) => {
        toast.info(`Moving Camera ${direction.toUpperCase()}...`);
    };

    const handleZoom = (delta: number) => {
        setZoomLevel(prev => Math.max(1, Math.min(10, prev + delta)));
        toast.info(`Zoom: ${Math.round((zoomLevel + delta) * 100)}%`);
    };

    const handleSnapshot = () => {
        toast.success('Snapshot saved to gallery');
    };

    const handleStartRecording = () => {
        toast.success('Manual recording started');
    };

    const handleExportClip = () => {
        toast.info('Exporting clip... (30 second default)');
    };

    const handleAcknowledgeEvent = (eventId: number) => {
        setEvents(prev => prev.map(e => e.id === eventId ? { ...e, acknowledged: true } : e));
        toast.success('Event acknowledged');
    };

    const handleRefreshFeed = () => {
        toast.info('Refreshing camera feed...');
    };

    const getEventIcon = (type: string) => {
        switch (type) {
            case 'person': return User;
            case 'vehicle': return Truck;
            case 'safety': return AlertTriangle;
            case 'motion': return Activity;
            case 'fire': return Siren;
            case 'system': return Settings;
            case 'wildlife': return Eye;
            case 'weather': return Sun;
            default: return Activity;
        }
    };

    return (
        <div className={`fixed inset-0 z-[100] ${isDarkMode ? 'bg-slate-900' : 'bg-white'} overflow-y-auto`}>
            {/* Sticky Header with Back Button - Same as NewTicketModal */}
            <div className={`sticky top-0 z-10 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                    }}
                    className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                    <ChevronLeft className="w-6 h-6" />
                    <span className="text-base font-medium">Back</span>
                </button>

                <div className="flex flex-col items-center">
                    <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Surveillance Center</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        <span className={`text-xs font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            {cameras.filter(c => c.status !== 'offline').length}/{cameras.length} ONLINE
                        </span>
                        {criticalCount > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                {criticalCount} CRITICAL
                            </span>
                        )}
                    </div>
                </div>

                <button
                    onClick={() => setShowSettings(!showSettings)}
                    className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                >
                    <Settings className="w-5 h-5" />
                </button>
            </div>

            {/* Main Content - Added safe area padding */}
            <div className="p-4 pb-[max(6rem,calc(6rem+env(safe-area-inset-bottom)))]">
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cameras</div>
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{cameras.length}</div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Recording</div>
                        <div className={`text-lg font-bold text-red-500`}>{cameras.filter(c => c.status === 'recording').length}</div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Alerts</div>
                        <div className={`text-lg font-bold ${criticalCount > 0 ? 'text-red-500' : warningCount > 0 ? 'text-yellow-500' : 'text-emerald-500'}`}>
                            {criticalCount + warningCount}
                        </div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Storage</div>
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>68%</div>
                    </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center justify-between mb-4">
                    <div className={`flex gap-1 p-1 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        {[
                            { mode: 'single', icon: Smartphone, label: 'Single' },
                            { mode: 'quad', icon: Grid, label: 'Quad' },
                            { mode: 'grid', icon: Grid, label: 'All' },
                        ].map(({ mode, icon: Icon, label }) => (
                            <button
                                key={mode}
                                onClick={() => setViewMode(mode as any)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${viewMode === mode
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'} ${notificationsEnabled ? 'text-blue-500' : 'text-gray-400'}`}
                        >
                            {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={handleRefreshFeed}
                            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                        >
                            <RefreshCw className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Camera Feed */}
                <div className={`relative rounded-2xl overflow-hidden mb-4 ${isDarkMode ? 'bg-black' : 'bg-gray-900'}`} style={{ aspectRatio: '16/9' }}>
                    {activeCam.status === 'offline' ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                                    <Wifi className="w-10 h-10 text-red-500" />
                                </div>
                                <p className="text-red-500 font-mono text-sm">CAMERA OFFLINE</p>
                                <p className="text-gray-500 text-xs mt-1">Check connection</p>
                                <button
                                    onClick={handleRefreshFeed}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                                >
                                    Retry Connection
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Simulated Video Background */}
                            <div
                                className={`absolute inset-0 transition-all duration-500 ${isThermalMode ? 'hue-rotate-180 saturate-200' : ''
                                    } ${isNightVision ? 'brightness-150 contrast-125' : ''}`}
                                style={{
                                    background: isThermalMode
                                        ? 'linear-gradient(180deg, #000 0%, #200 50%, #400 100%)'
                                        : isNightVision
                                            ? 'linear-gradient(180deg, #001a00 0%, #003300 100%)'
                                            : 'radial-gradient(circle at 50% 50%, #27272a 0%, #18181b 100%)',
                                    transform: `scale(${zoomLevel})`
                                }}
                            >
                                <div className="absolute inset-0 opacity-10 mix-blend-overlay" style={{
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'
                                }}></div>
                            </div>

                            {/* Camera Info HUD */}
                            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                                    <span className="text-white text-xs font-mono">REC</span>
                                    <span className="text-gray-400 text-xs">• {activeCam.resolution}</span>
                                </div>
                                <h3 className="text-white font-bold text-lg drop-shadow-lg">{activeCam.name}</h3>
                                <p className="text-gray-300 text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {activeCam.location}
                                </p>
                            </div>

                            {/* Status Indicators */}
                            <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                                <div className="flex items-center gap-2 px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                                    <Battery className={`w-4 h-4 ${activeCam.battery! < 20 ? 'text-red-500' : 'text-green-500'}`} />
                                    <span className="text-white text-xs font-mono">{activeCam.battery}%</span>
                                </div>
                                <div className="flex items-center gap-2 px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                                    <Wifi className={`w-4 h-4 ${activeCam.signal! < 30 ? 'text-red-500' : activeCam.signal! < 70 ? 'text-yellow-500' : 'text-green-500'}`} />
                                    <span className="text-white text-xs font-mono">{activeCam.signal}%</span>
                                </div>
                                {audioEnabled && (
                                    <div className="flex items-center gap-2 px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                                        <Volume2 className="w-4 h-4 text-blue-400" />
                                        <span className="text-white text-xs font-mono">AUDIO</span>
                                    </div>
                                )}
                            </div>

                            {/* Center Icon */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-16 h-16 rounded-full border border-white/20 flex items-center justify-center">
                                    {activeCam.type === 'drone' ? (
                                        <Plane className="w-8 h-8 text-sky-400" />
                                    ) : activeCam.type === 'thermal' ? (
                                        <Thermometer className="w-8 h-8 text-orange-400" />
                                    ) : (
                                        <Video className="w-8 h-8 text-gray-500" />
                                    )}
                                </div>
                            </div>

                            {/* Timestamp */}
                            <div className="absolute bottom-3 left-3">
                                <div className="px-2 py-1 rounded bg-black/60 backdrop-blur-sm">
                                    <span className="text-white text-xs font-mono">
                                        {new Date().toLocaleTimeString()} • {new Date().toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="absolute bottom-3 right-3 flex gap-2">
                                <button
                                    onClick={handleSnapshot}
                                    className="p-2 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80"
                                >
                                    <Camera className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={handleStartRecording}
                                    className="p-2 rounded-lg bg-red-600 text-white hover:bg-red-500"
                                >
                                    <Radio className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    className="p-2 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80"
                                >
                                    <Maximize2 className="w-5 h-5" />
                                </button>
                            </div>

                            {/* PTZ Controls (for PTZ cameras) */}
                            {activeCam.type === 'ptz' && showPTZ && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="absolute bottom-16 right-3 p-3 bg-black/80 backdrop-blur-xl rounded-2xl"
                                >
                                    <div className="flex flex-col items-center gap-1">
                                        <button onClick={() => handlePTZ('up')} className="p-2 hover:bg-white/20 rounded-full">
                                            <ChevronUp className="w-6 h-6 text-white" />
                                        </button>
                                        <div className="flex gap-4">
                                            <button onClick={() => handlePTZ('left')} className="p-2 hover:bg-white/20 rounded-full">
                                                <ChevronLeft className="w-6 h-6 text-white" />
                                            </button>
                                            <button onClick={() => handlePTZ('home')} className="p-2 hover:bg-white/20 rounded-full bg-white/10">
                                                <Crosshair className="w-5 h-5 text-white" />
                                            </button>
                                            <button onClick={() => handlePTZ('right')} className="p-2 hover:bg-white/20 rounded-full">
                                                <ChevronRight className="w-6 h-6 text-white" />
                                            </button>
                                        </div>
                                        <button onClick={() => handlePTZ('down')} className="p-2 hover:bg-white/20 rounded-full">
                                            <ChevronDown className="w-6 h-6 text-white" />
                                        </button>
                                        <div className="flex gap-2 mt-2 pt-2 border-t border-white/20">
                                            <button onClick={() => handleZoom(-0.5)} className="p-1.5 hover:bg-white/20 rounded">
                                                <ZoomOut className="w-4 h-4 text-white" />
                                            </button>
                                            <span className="text-white text-xs font-mono self-center">{Math.round(zoomLevel * 100)}%</span>
                                            <button onClick={() => handleZoom(0.5)} className="p-1.5 hover:bg-white/20 rounded">
                                                <ZoomIn className="w-4 h-4 text-white" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </>
                    )}
                </div>

                {/* Camera Quick Select Strip */}
                <div className={`flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4`}>
                    {cameras.map(cam => (
                        <button
                            key={cam.id}
                            onClick={() => setActiveCamId(cam.id)}
                            className={`relative flex-shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-all ${activeCamId === cam.id
                                ? 'border-blue-500 ring-2 ring-blue-500/30'
                                : isDarkMode ? 'border-slate-700 opacity-60 hover:opacity-100' : 'border-gray-200 opacity-60 hover:opacity-100'
                                }`}
                        >
                            <div className={`absolute inset-0 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-200'} flex items-center justify-center`}>
                                {cam.type === 'thermal' ? (
                                    <Thermometer className="w-5 h-5 text-orange-500" />
                                ) : cam.type === 'drone' ? (
                                    <Plane className="w-5 h-5 text-sky-500" />
                                ) : (
                                    <Video className={`w-5 h-5 ${cam.status === 'offline' ? 'text-red-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                                )}
                            </div>
                            <div className="absolute bottom-0 inset-x-0 bg-black/70 px-1 py-0.5">
                                <p className="text-white text-[9px] font-mono text-center truncate">{cam.name}</p>
                            </div>
                            {cam.status === 'recording' && (
                                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                            )}
                            {cam.status === 'offline' && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                    <span className="text-red-500 text-[8px] font-bold">OFFLINE</span>
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Controls Bar */}
                <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {recordingMode === 'live' ? 'LIVE' : 'PLAYBACK'}
                            </span>
                            <div className={`flex gap-1 p-0.5 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                <button
                                    onClick={() => setRecordingMode('live')}
                                    className={`px-2 py-1 rounded text-xs ${recordingMode === 'live' ? 'bg-red-500 text-white' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                >
                                    Live
                                </button>
                                <button
                                    onClick={() => setRecordingMode('playback')}
                                    className={`px-2 py-1 rounded text-xs ${recordingMode === 'playback' ? 'bg-blue-500 text-white' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                >
                                    Playback
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {activeCam.type === 'ptz' && (
                                <button
                                    onClick={() => setShowPTZ(!showPTZ)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${showPTZ ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                        }`}
                                >
                                    <Move className="w-3 h-3" /> PTZ
                                </button>
                            )}
                            <button
                                onClick={() => setIsThermalMode(!isThermalMode)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${isThermalMode ? 'bg-orange-600 text-white' : isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                    }`}
                            >
                                <Thermometer className="w-3 h-3" /> Thermal
                            </button>
                            <button
                                onClick={() => setIsNightVision(!isNightVision)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${isNightVision ? 'bg-green-600 text-white' : isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                    }`}
                            >
                                <Moon className="w-3 h-3" /> Night
                            </button>
                            <button
                                onClick={() => setAudioEnabled(!audioEnabled)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${audioEnabled ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                    }`}
                            >
                                {audioEnabled ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                            </button>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className={`h-8 rounded-lg relative overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div className="absolute inset-0 flex items-end justify-between px-1 pb-1 opacity-30">
                            {[...Array(24)].map((_, i) => (
                                <div key={i} className={`w-px ${i % 4 === 0 ? 'h-3 bg-white' : 'h-1.5 bg-white/50'}`}></div>
                            ))}
                        </div>
                        {/* Event markers on timeline */}
                        {events.filter(e => e.camId === activeCamId).map((e, i) => (
                            <div
                                key={e.id}
                                className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${e.severity === 'critical' ? 'bg-red-500' : e.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                                    }`}
                                style={{ left: `${10 + i * 8}%` }}
                            ></div>
                        ))}
                        <div className="absolute top-0 bottom-0 w-0.5 bg-blue-500 left-[85%] z-10">
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 px-1 py-0.5 bg-blue-500 text-white text-[8px] rounded">NOW</div>
                        </div>
                    </div>

                    {/* Playback Controls */}
                    <div className="flex items-center justify-center gap-6 mt-3">
                        <button className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                            <SkipBack className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setIsPlaying(!isPlaying)}
                            className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-500 transition-colors shadow-lg"
                        >
                            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                        </button>
                        <button className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                            <SkipForward className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Events Section */}
                <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    <div className={`p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Events & Alerts</h3>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {events.filter(e => !e.acknowledged).length} unread
                            </span>
                        </div>
                        <div className="flex gap-1 overflow-x-auto pb-1">
                            {['all', 'critical', 'motion', 'person', 'vehicle'].map(type => (
                                <button
                                    key={type}
                                    onClick={() => setFilterType(type)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${filterType === type
                                        ? 'bg-blue-600 text-white'
                                        : isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                                        }`}
                                >
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto p-2 space-y-2">
                        {filteredEvents.map(event => {
                            const EventIcon = getEventIcon(event.type);
                            return (
                                <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`p-3 rounded-xl flex items-start gap-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-white'
                                        } ${!event.acknowledged ? 'ring-1 ring-blue-500/50' : ''}`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${event.severity === 'critical' ? 'bg-red-500/20 text-red-500' :
                                        event.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-500' :
                                            'bg-blue-500/20 text-blue-500'
                                        }`}>
                                        <EventIcon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {event.message}
                                            </p>
                                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {event.time}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Cam {event.camId}
                                            </span>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${event.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                                event.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                                                    'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                {event.severity.toUpperCase()}
                                            </span>
                                            {!event.acknowledged && (
                                                <button
                                                    onClick={() => handleAcknowledgeEvent(event.id)}
                                                    className="text-xs text-blue-500 font-medium"
                                                >
                                                    Acknowledge
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <button className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-100'}`}>
                                        <Share2 className="w-4 h-4 text-gray-400" />
                                    </button>
                                </motion.div>
                            );
                        })}
                        {filteredEvents.length === 0 && (
                            <div className="text-center py-8">
                                <Eye className={`w-12 h-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No events for this filter</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                    <button
                        onClick={handleExportClip}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Download className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Export Clip</span>
                    </button>
                    <button
                        onClick={handleSnapshot}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Camera className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Snapshot</span>
                    </button>
                    <button
                        onClick={() => toast.info('Opening camera settings...')}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Settings className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Settings</span>
                    </button>
                </div>

                {/* Camera Details Card */}
                <div className={`mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    <h3 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Camera Details</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Camera Type</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeCam.type.toUpperCase()}
                            </p>
                        </div>
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Resolution</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeCam.resolution}
                            </p>
                        </div>
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Motion</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeCam.lastMotion}
                            </p>
                        </div>
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Storage Used</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeCam.storageUsed}%
                            </p>
                        </div>
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Recording</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeCam.recordingHours}h
                            </p>
                        </div>
                        <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Night Vision</p>
                            <p className={`text-sm font-medium ${activeCam.nightVision ? 'text-green-500' : 'text-red-500'}`}>
                                {activeCam.nightVision ? 'Enabled' : 'Disabled'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
